import { useState, useEffect, useRef, useCallback } from 'react';
import { useGridApiRef } from '@mui/x-data-grid-pro';
import { useQueryClient } from '@tanstack/react-query';
import { useAddUser, useDeleteUser, useGetRoles, useUpdateUser, getAllUsersList } from '../api/vegApl';
import { useForm } from 'react-hook-form';
import { Providers, SimpleProvider, ProviderState } from '@microsoft/mgt-element';
import { useSnackbar } from '../contexts/SnackbarContext';
import { useConfirm } from '../contexts/confirmDialogContext';
import { useCommonStyles } from '../styles/GenericStyle';
import { useResponsiveHeaderHeight } from './useResponsiveHeaderHeight';
import { usePeoplePickerStyles } from './usePeoplePickerStyles';
import { useDropdownPosition } from './useDropdownPosition';
import { usePersistedGridState } from './usePersistedGridState';
import { MessageConstant } from '../utils/MessageConst';
import { GenericConstant } from '../utils/GenericConstant';
import { useGlobalState } from '../contexts/GlobalState';
import type { ListValueUser } from '../model/commonType';
import { useNavigate } from 'react-router-dom';

export const useUserManagementController = () => {
    const { state: globalState, setBreadcrumbsText, sites } = useGlobalState();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const [mode, setMode] = useState<string>('view');
    const [allModules, setAllModules] = useState<any[]>([]);
    const [allRoles, setAllRoles] = useState<any[]>([]);
    const [userGridData, setUsersGridData] = useState<any[]>([]);
    const [isFilterVisible, setFilterVisible] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isEmailInvalid, setisEmailInvalid] = useState<boolean>(false);
    const [user_data, setUserData] = useState<any>({ user_email: '', user_name: '' });
    const apiRef = useGridApiRef();
    const { applyColumnWidths, onColumnWidthChange } = usePersistedGridState('user-management-grid');
    const classes = useCommonStyles();
    const headerHeight = useResponsiveHeaderHeight(48);
    const [sortModel, setSortModel] = useState<any[]>([{ field: 'user', sort: 'asc' }]);
    const [filterValues, setFilterValues] = useState<any[]>([]);
    const [gridUserData, setGridUserData] = useState<any>({ user_id: '' });
    const [filterDel, setFilterDel] = useState<any>({});
    const handleSortModelChange = useCallback((newSortModel: any) => setSortModel(newSortModel), []);
    const [listValues, setListValue] = useState<ListValueUser>({ sitesData: [], allRoles: [], allModules: [] });
    const peoplePickerRef = useRef(null);
    usePeoplePickerStyles(peoplePickerRef, mode === 'Add', user_data.user_email);
    const { menuPosition, calculatePosition } = useDropdownPosition(300);

    Providers.globalProvider = new SimpleProvider((_scopes) => {
        let accessToken = globalState?.accessToken;
        return new Promise((resolve) => { resolve(accessToken); });
    });
    Providers.globalProvider.setState(ProviderState.SignedIn);
    const { showSnackbar } = useSnackbar();
    const { confirm } = useConfirm();

    const { mutate: addUser } = useAddUser({
        mutation: {
            onSuccess: (data: any) => { showSnackbar({ message: data?.data?.message, severity: 'success' }); getAllUsersListCall({}); setMode('view'); setIsLoading(false); },
            onError: (error: any) => { const detail = error?.response?.data?.detail; const errorMessage = typeof detail === 'string' ? detail : MessageConstant.USERPERMISSIONUPDATEFAILED; showSnackbar({ message: errorMessage, severity: 'error' }); setIsLoading(false); }
        }
    });

    const { mutate: updateUser } = useUpdateUser({
        mutation: {
            // Refetch the logged-in user so an edit to their own site allocation is picked up immediately.
            onSuccess: (data: any) => { showSnackbar({ message: data?.data?.message, severity: 'success' }); queryClient.invalidateQueries({ queryKey: ['email'] }); getAllUsersListCall({}); setMode('view'); setIsLoading(false); },
            onError: (error: any) => { const detail = error?.response?.data?.detail; const errorMessage = typeof detail === 'string' ? detail : MessageConstant.USERPERMISSIONUPDATEFAILED; showSnackbar({ message: errorMessage, severity: 'error' }); setIsLoading(false); }
        }
    });

    const { mutate: deleteUser } = useDeleteUser({
        mutation: {
            onSuccess: (res: any) => { showSnackbar({ message: res?.data?.message, severity: 'success' }); getAllUsersListCall({}); },
            onError: (error: any) => { const detail = error?.response?.data?.detail; const errorMessage = typeof detail === 'string' ? detail : MessageConstant.USERPERMISSIONUPDATEFAILED; showSnackbar({ message: errorMessage, severity: 'error' }); }
        }
    });

    const { mutate: getAllUsersListCall } = getAllUsersList({
        mutation: {
            onSuccess: (data: any) => { setIsLoading(false); setUsersGridData(data?.data ?? []); },
            onError: () => { setIsLoading(false); }
        }
    });

    const form = useForm({ defaultValues: { globalRole: [], user_roles: [], sites: [] }, mode: 'onChange' });

    const { data: moduleWithRoles = [] } = useGetRoles({ query: { queryKey: ['moduleWithRoles'], select: (d) => d?.data ?? [], refetchOnWindowFocus: false, cacheTime: 0 } });

    useEffect(() => {
        if (!Array.isArray(moduleWithRoles) || moduleWithRoles.length === 0) return;
        let allModulesList = [];
        let allRolesList = [];
        for (const element of moduleWithRoles) {
            allModulesList.push({ module_id: element?.module_id, name: element?.name });
            let allRoles = element?.roles || [];
            for (const r of allRoles) allRolesList.push({ role_id: r?.role_id, name: r?.name });
        }
        setAllModules(allModulesList);
        setAllRoles(allRolesList);
    }, [moduleWithRoles]);

    useEffect(() => {
        const newArray = sites.map(item => ({ ...item, site_name: [item.site_name, item.full_code].filter(Boolean).join('-') }));
        setListValue({ sitesData: newArray ?? [], allRoles, allModules });
    }, [allModules, allRoles, sites]);

    useEffect(() => {
        const lastCrumb = mode === 'Add' || mode == 'Edit' ? `${mode} User` : '';
        setBreadcrumbsText(['Home', 'User management', lastCrumb]);
        if (mode == 'Add') { setUserData({ user_email: '', user_name: '' }); form.reset({ sites: [], user_roles: [], globalRole: [] }); }
        setisEmailInvalid(false);
    }, [mode]);

    useEffect(() => {
        setBreadcrumbsText(['Home', 'User management']);
        setIsLoading(true);
        getAllUsersListCall({});
    }, []);

    const deleteUserConfirm = async (userEmail: any) => {
        const ok: any = await confirm({ title: GenericConstant.deleteUserDialogTitle, description: GenericConstant.deleteUserDialogMsg, confirmText: 'Ok', cancelText: 'Cancel', severity: 'error' });
        if (ok) deleteUser({ data: userEmail });
    };

    const editUserBindData = (params: any) => {
        setMode('Edit');
        let tmpRoles = params?.row?.roles ?? [];
        let user_roles = [];
        for (const element of moduleWithRoles) if (element?.name !== 'Global') user_roles.push(...getIdsByRoleNames(element, tmpRoles));
        let userSites = params?.row?.sites ?? [];
        const siteCode = userSites.map((siteName: any) => { const match = sites.find(item => item?.site_name === siteName); return match ? match?.site_code : null; });
        form.reset({ sites: siteCode ?? [], user_roles: user_roles, globalRole: tmpRoles.filter((roleName: any) => roleName === 'Admin' || roleName === 'Breeder') });
        setUserData({ user_email: params?.row?.user_email ?? '', user_name: params?.row?.user_name ?? '' });
        setGridUserData({ user_id: params?.row?.user_id ?? '' });
    };

    const getIdsByRoleNames = (module: any, roleNames: any) => {
        if (!module || !Array.isArray(module?.roles)) return [];
        const nameSet = new Set((roleNames || []).map((n: any) => (n || '').toLowerCase()));
        return module.roles.filter((r: any) => nameSet?.has((r.name || '').toLowerCase())).map((r: any) => `${module?.module_id}~${r?.role_id}~${r?.name}`);
    };

    const editConfirmation = async (data: any) => {
        const ok: any = await confirm({ title: GenericConstant.editUserBlankRoleTitle, description: GenericConstant.editUserBlankRoleMessage, confirmText: 'OK', cancelText: 'Cancel', severity: 'error' });
        if (ok) { (data as any).inConfirmed = true; onSubmit(data); }
    };

    const onSubmit = (data: any) => {
        if (!navigator.onLine) { showSnackbar({ message: 'You are offline. Please check your internet connection.', severity: 'error' }); return; }
        const trimmedEmail = (user_data?.user_email ?? '').trim();
        const isValidEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail);
        if (!trimmedEmail || !isValidEmail) { setisEmailInvalid(true); return; }
        const modWithRoles = data?.user_roles ?? [];
        const globalRoles = data?.globalRole ?? [];
        const sitesArr = data?.sites ?? [];
        const moduleWithPermissions: any[] = [];
        let add_edit_user_request: any;
        let globalRoleArr: any = globalRoles?.length ? [{ module_id: 1, roles: [] }] : [];
        if (modWithRoles?.length == 0 && globalRoles?.length == 0 && !data?.inConfirmed) { editConfirmation(data); return; }
        setIsLoading(true);
        for (const element of globalRoles) { let globalRole = { role_id: element == 'Admin' ? 1 : 2, role_name: element, is_global: true, sites: sitesArr }; globalRoleArr[0].roles.push(globalRole); }
        for (const el of modWithRoles) {
            let objKey = el?.split('~') ?? [];
            let existingModule = moduleWithPermissions?.findIndex((v) => v?.module_id == objKey[0]);
            if (existingModule == -1) {
                const existingModuleObj = { module_id: Number(objKey[0]), roles: [{ role_id: Number(objKey[1]), role_name: objKey[2], is_global: false, sites: sitesArr }] };
                moduleWithPermissions.push(existingModuleObj);
            } else { moduleWithPermissions[existingModule]?.roles.push({ role_id: Number(objKey[1]), role_name: objKey[2], is_global: false, sites: sitesArr }); }
        }
        let by_field = mode == 'Add' ? 'created_by' : 'updated_by';
        add_edit_user_request = { user_email: user_data?.user_email, user_name: user_data?.user_name, user_roles: [...moduleWithPermissions, ...globalRoleArr], [by_field]: globalState?.user_id_db };
        if (mode == 'Edit') add_edit_user_request.user_id = Number(gridUserData?.user_id);
        mode == 'Add' ? addUser({ data: add_edit_user_request }) : updateUser({ data: add_edit_user_request });
    };

    const handleSelectionChanged = (e: Event) => {
        const detail = (e as CustomEvent)?.detail;
        const peopleFromDetail = Array.isArray(detail) ? detail : [];
        if (peopleFromDetail.length === 0) { setUserData({ user_email: '', user_name: '' }); setisEmailInvalid(true); return; }
        const selected = peopleFromDetail[0] ?? {};
        const rawEmail = (selected?.mail || selected?.userPrincipalName || selected?.id || '').toString().trim();
        const rawName = (selected?.displayName || rawEmail || '').toString().trim();
        const isValidEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(rawEmail);
        if (!rawEmail || !isValidEmail) { setUserData({ user_email: '', user_name: '' }); setisEmailInvalid(true); return; }
        setUserData({ user_email: rawEmail, user_name: rawName }); setisEmailInvalid(false);
    };

    const backButtonClick = () => {
        if (mode === 'view') {
            navigate(-1);
            return;
        }
        setMode('view');
    };

    const handleDeleteFilter = useCallback((filter: any) => { setFilterDel(filter); }, []);

    return {
        mode, setMode, allModules, allRoles, userGridData, setUsersGridData, isFilterVisible, setFilterVisible, isLoading, setIsLoading, isEmailInvalid, setisEmailInvalid, user_data, setUserData, apiRef, applyColumnWidths, onColumnWidthChange, classes, headerHeight, sortModel, handleSortModelChange, filterValues, setFilterValues, filterDel, listValues, setListValue, sites, peoplePickerRef, menuPosition, calculatePosition, showSnackbar, confirm, addUser, updateUser, deleteUser, getAllUsersListCall, form, moduleWithRoles, deleteUserConfirm, editUserBindData, editConfirmation, onSubmit, handleSelectionChanged, backButtonClick, handleDeleteFilter, getIdsByRoleNames, roleSelected: globalState?.roleSelected ?? ''
    };
};

export default useUserManagementController;
